import React, { useEffect, useState, useRef } from 'react';
import { trackPixelEvent } from './MetaPixel';
type PackId = 'primaria' | 'completo' | 'secundaria';
const PACKS: Array<{
  id: PackId;
  emoji: string;
  title: string;
  description: string;
  oldPrice: string;
  price: string;
  priceValue: number;
  ctaLabel: string;
  ctaSubtle: string;
}> = [{
  id: 'primaria',
  emoji: '📘',
  title: 'PRIMARIA',
  description: '6 tomos + 90 actividades + 6 bonus',
  oldPrice: '$49.990',
  price: '$16.990',
  priceValue: 16990,
  ctaLabel: 'SÍ, QUIERO PRIMARIA →',
  ctaSubtle: 'Ideal para docentes de 1° a 6° grado'
}, {
  id: 'completo',
  emoji: '🏆',
  title: 'PACK COMPLETO',
  description: '12 tomos + 180 actividades + 6 bonus + 2 EBOOKS ÉLITE',
  oldPrice: '$99.980',
  price: '$27.990',
  priceValue: 27990,
  ctaLabel: 'SÍ, QUIERO PACK COMPLETO →',
  ctaSubtle: 'La opción más elegida por docentes que enseñan en los dos niveles'
}, {
  id: 'secundaria',
  emoji: '📗',
  title: 'SECUNDARIA',
  description: '6 tomos + 90 actividades + 6 bonus',
  oldPrice: '$49.990',
  price: '$18.990',
  priceValue: 18990,
  ctaLabel: 'SÍ, QUIERO SECUNDARIA →',
  ctaSubtle: 'Ideal para docentes de 1° a 6° año'
}];
export function PricingSection() {
  const [selected, setSelected] = useState<PackId>('completo');
  const selectedPack = PACKS.find(p => p.id === selected) ?? PACKS[1];
  const sectionRef = useRef<HTMLElement>(null);
  const viewContentFiredRef = useRef(false);
  // Fire Meta Pixel ViewContent when pricing section enters the viewport.
  useEffect(() => {
    if (!sectionRef.current || viewContentFiredRef.current) return;
    const node = sectionRef.current;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewContentFiredRef.current) {
          viewContentFiredRef.current = true;
          trackPixelEvent('ViewContent', {
            content_name: 'Pricing Section',
            content_category: 'Pack de Inglés 360',
            content_ids: PACKS.map(p => p.id),
            currency: 'ARS',
            value: selectedPack.priceValue
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [selectedPack.priceValue]);
  const handleSelect = (pack: (typeof PACKS)[number]) => {
    setSelected(pack.id);
    trackPixelEvent('AddToCart', {
      content_name: pack.title,
      content_ids: [pack.id],
      content_type: 'product',
      currency: 'ARS',
      value: pack.priceValue
    });
  };
  const handleInitiateCheckout = () => {
    trackPixelEvent('InitiateCheckout', {
      content_name: selectedPack.title,
      content_ids: [selectedPack.id],
      content_category: 'Pack de Inglés 360',
      contents: [{
        id: selectedPack.id,
        quantity: 1,
        item_price: selectedPack.priceValue
      }],
      num_items: 1,
      currency: 'ARS',
      value: selectedPack.priceValue
    });
  };
  return <section id="precios" ref={sectionRef} className="py-10 md:py-16 px-4 md:px-6 bg-gray-50" data-id="element-117">
      <div className="max-w-2xl mx-auto" data-id="element-118">
        <div className="text-center mb-6 md:mb-8" data-id="element-119">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-120">
            Elegí tu Pack de Inglés:
          </h2>
          <p className="text-gray-500 text-sm md:text-base" data-id="element-121">
            Todas las opciones incluyen los 6 bonus estratégicos
          </p>
        </div>

        <div className="flex flex-col gap-3 md:gap-4" data-id="element-122">
          {PACKS.map(pack => {
          const isSelected = selected === pack.id;
          const isFeatured = pack.id === 'completo';
          return <button key={pack.id} type="button" onClick={() => handleSelect(pack)} aria-pressed={isSelected} className={['group w-full text-left rounded-2xl border-2 transition-all duration-200', 'px-4 md:px-6 py-4 md:py-5 flex items-center gap-3 md:gap-4', isFeatured ? isSelected ? 'bg-blue-50 border-blue-600 shadow-md' : 'bg-blue-50/60 border-blue-200 hover:border-blue-400' : isSelected ? 'bg-white border-blue-600 shadow-md' : 'bg-white border-gray-200 hover:border-gray-300'].join(' ')} data-id="element-123">
                {/* Radio indicator */}
                <span className={['shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors', isSelected ? 'border-blue-600' : 'border-gray-300 group-hover:border-gray-400'].join(' ')} aria-hidden="true" data-id="element-124">
                  {isSelected && <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-blue-600" data-id="element-125" />}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-126">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 md:gap-x-3 mb-0.5" data-id="element-127">
                    <span className="text-xl md:text-2xl leading-none" data-id="element-128">
                      {pack.emoji}
                    </span>
                    <span className="font-extrabold text-gray-900 text-base md:text-xl tracking-tight" data-id="element-129">
                      {pack.title}
                    </span>
                    {isFeatured && <>
                        <span className="inline-flex items-center gap-1 bg-blue-600 text-white text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-130">
                          ⭐ MÁS ELEGIDO
                        </span>
                        <span className="hidden sm:inline-flex bg-blue-100 text-blue-700 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-131">
                          AHORRÁS $7.990
                        </span>
                      </>}
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 leading-snug" data-id="element-132">
                    {pack.description}
                  </p>
                  {isFeatured && <p className="sm:hidden mt-1.5 inline-flex bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-133">
                      AHORRÁS $7.990
                    </p>}
                </div>

                {/* Pricing */}
                <div className="shrink-0 text-right" data-id="element-134">
                  <p className="line-through text-gray-400 text-xs md:text-sm leading-tight" data-id="element-135">
                    {pack.oldPrice}
                  </p>
                  <p className={['font-extrabold leading-tight text-xl md:text-3xl', isFeatured ? 'text-blue-600' : 'text-gray-900'].join(' ')} data-id="element-136">
                    {pack.price}
                  </p>
                </div>
              </button>;
        })}
        </div>

        {/* Main CTA */}
        <div className="mt-6 md:mt-8" data-id="element-137">
          <a href="#" onClick={handleInitiateCheckout} className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-base md:text-xl py-4 md:py-5 rounded-2xl shadow-lg transition-colors tracking-wide" data-id="element-138">
            {selectedPack.ctaLabel}
          </a>
          <p className="text-center italic text-gray-500 text-xs md:text-sm mt-3 md:mt-4 px-2" data-id="element-139">
            {selectedPack.ctaSubtle}
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-5 md:mt-6 text-[11px] md:text-xs text-gray-500" data-id="element-140">
          <span data-id="element-141">🔒 Pago 100% seguro</span>
          <span data-id="element-142">⚡ Acceso en 5 minutos</span>
          <span data-id="element-143">🛡️ Garantía 7 días</span>
          <span data-id="element-144">💳 Mercado Pago</span>
        </div>

        {/* PACK COMPLETO — Qué incluye */}
        <div className="mt-10 md:mt-12 bg-white border-2 border-blue-200 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-145">
          <div className="text-center mb-5" data-id="element-146">
            <span className="inline-block bg-blue-600 text-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full mb-3" data-id="element-147">
              🏆 PACK COMPLETO — $27.990
            </span>
            <h3 className="text-lg md:text-2xl font-extrabold text-gray-900 leading-tight" data-id="element-148">
              Esto es TODO lo que recibís en el Pack Completo
            </h3>
            <p className="text-gray-500 text-xs md:text-sm mt-2" data-id="element-149">
              La opción más elegida por docentes que enseñan en los dos niveles
            </p>
          </div>

          {/* Tomos + bonus */}
          <div className="grid sm:grid-cols-2 gap-3 mb-6" data-id="element-150">
            {['12 tomos completos (Pre-A1 → B2)', '180 actividades con secuencia didáctica', 'Primaria + Secundaria — todos los niveles', 'Solucionarios incluidos en cada tomo', '6 Bonus estratégicos de gestión docente', 'Acceso inmediato — PDF descargable'].map(item => <div key={item} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-151">
                <span className="text-blue-600 font-bold mt-0.5" data-id="element-152">✓</span>
                <span data-id="element-153">{item}</span>
              </div>)}
          </div>

          {/* EBOOKS ÉLITE — destacado */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 md:p-5" data-id="element-154">
            <p className="text-blue-700 font-extrabold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2" data-id="element-155">
              💎 Exclusivo del Pack Completo: 2 Ebooks Élite
            </p>
            <div className="space-y-3" data-id="element-156">
              <div className="flex items-start gap-3" data-id="element-157">
                <span className="shrink-0 w-8 h-8 rounded-lg bg-blue-600 text-white font-extrabold flex items-center justify-center text-sm" data-id="element-158">
                  1
                </span>
                <div data-id="element-159">
                  <p className="font-bold text-gray-900 text-sm md:text-base" data-id="element-160">
                    Ebook Élite 1 — Planificaciones Anuales listas
                  </p>
                  <p className="text-gray-600 text-xs md:text-sm leading-snug" data-id="element-161">
                    12 planificaciones anuales completas (una por cada
                    año/grado) en formato editable. Solo personalizás tu nombre,
                    escuela y fecha. Listas para entregar a directivos en
                    minutos.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-162">
                <span className="shrink-0 w-8 h-8 rounded-lg bg-blue-600 text-white font-extrabold flex items-center justify-center text-sm" data-id="element-163">
                  2
                </span>
                <div data-id="element-164">
                  <p className="font-bold text-gray-900 text-sm md:text-base" data-id="element-165">
                    Ebook Élite 2 — 12 Exámenes Integradores con Answer Key
                  </p>
                  <p className="text-gray-600 text-xs md:text-sm leading-snug" data-id="element-166">
                    Un examen integrador final por cada nivel (Pre-A1 hasta B2),
                    con solucionario y rúbrica de corrección incluidos. Para
                    evaluar el cierre de año sin armar nada desde cero.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Valor / ahorro */}
          <div className="mt-6 text-center" data-id="element-167">
            <p className="text-gray-500 text-sm" data-id="element-168">
              Valor real: <span className="line-through" data-id="element-169">$99.980</span>
            </p>
            <p className="text-3xl md:text-4xl font-extrabold text-blue-600 mt-1" data-id="element-170">
              Hoy: $27.990 ARS
            </p>
            <p className="text-green-600 text-xs md:text-sm font-bold mt-1" data-id="element-171">
              Ahorrás $71.990 (72% OFF)
            </p>
          </div>
        </div>
      </div>
    </section>;
}